// src/services/bolService.js - UPDATED FOR PICKUP & DELIVERY SIGNATURES
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

export const generateProfessionalBOL = async (shipment, signatureData, currentUser) => {
  if (!currentUser) {
    throw new Error('User must be authenticated to generate BOL');
  }

  try {
    if (!shipment || !shipment.id || !shipment.invoiceNumber) {
      throw new Error('Shipment data is required');
    }

    console.log('Generating professional BOL with embedded photos and signatures...');

    // Get the latest shipment data
    const shipmentDoc = await getDoc(doc(db, 'shipments', shipment.id));
    const latestShipment = { id: shipmentDoc.id, ...shipmentDoc.data() };

    // ✅ FIXED: Extract displayId from the passed shipment data, not just Firestore
    const displayId = shipment.displayId || latestShipment.displayId || generateDisplayId(shipment.id, 0);

    // Extract all photos and comments data
    const signatures = latestShipment.signatures || {};
    
    const pickupPhotos = signatures.pickup_photos || [];
    const deliveryPhotos = signatures.delivery_photos || [];
    const pickupComment = signatures.pickup_comment || '';
    const deliveryComment = signatures.delivery_comment || '';
    
    // ✅ UPDATED: Get ALL signature data for pickup and delivery
    const pickupCustomerSignature = signatures.pickup_customer?.signature;
    const pickupDriverSignature = signatures.pickup_driver?.signature;
    const pickupCustomerSkipped = signatures.pickup_customer?.skipped;
    
    const deliveryCustomerSignature = signatures.delivery_customer?.signature;
    const deliveryDriverSignature = signatures.delivery_driver?.signature;
    const deliveryCustomerSkipped = signatures.delivery_customer?.skipped;

    console.log('BOL Generation Data:', {
      displayId: displayId,
      pickupPhotosCount: pickupPhotos.length,
      deliveryPhotosCount: deliveryPhotos.length,
      pickupComment,
      deliveryComment,
      // Pickup signatures
      hasPickupCustomerSignature: !!pickupCustomerSignature,
      hasPickupDriverSignature: !!pickupDriverSignature,
      pickupCustomerSkipped,
      // Delivery signatures
      hasDeliveryCustomerSignature: !!deliveryCustomerSignature,
      hasDeliveryDriverSignature: !!deliveryDriverSignature,
      deliveryCustomerSkipped,
      // Debug info
      passedShipmentDisplayId: shipment.displayId,
      firestoreDisplayId: latestShipment.displayId,
      generatedDisplayId: generateDisplayId(shipment.id, 0)
    });

    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage([612, 792]);
    const { width, height } = page.getSize();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Colors
    const black = rgb(0, 0, 0);
    const darkGray = rgb(0.3, 0.3, 0.3);
    const lightGray = rgb(0.95, 0.95, 0.95);

    // Helper function to sanitize text for PDF
    const sanitizeText = (text) => {
      if (typeof text !== 'string') return String(text || '');
      return text.replace(/[^\x00-\x7F]/g, '');
    };

    // Helper function to add new page
    const addNewPage = () => {
      page = pdfDoc.addPage([612, 792]);
      return { width, height };
    };

    let yPosition = height - 50;

    // Function to check if we need a new page
    const checkPageSpace = (requiredSpace = 50) => {
      if (yPosition < requiredSpace) {
        addNewPage();
        yPosition = height - 50;
        return true;
      }
      return false;
    };

    // Function to draw form sections
    const drawFormSection = (title, fields, columns = 1) => {
      checkPageSpace(80);
      
      // Section header with background
      page.drawRectangle({
        x: 40,
        y: yPosition - 20,
        width: width - 80,
        height: 20,
        color: lightGray,
      });

      page.drawText(sanitizeText(title), {
        x: 50,
        y: yPosition - 14,
        size: 10,
        font: boldFont,
        color: black,
      });

      yPosition -= 30;

      // Draw fields
      fields.forEach((field, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        const fieldWidth = (width - 100) / columns;
        const x = 50 + (col * fieldWidth);
        const fieldY = yPosition - (row * 25);

        page.drawText(sanitizeText(field.label), {
          x: x,
          y: fieldY,
          size: 8,
          font: boldFont,
          color: darkGray,
        });

        // ✅ FIXED: Ensure we're using the actual value, not falling back to placeholder
        const value = sanitizeText(field.value) || '_________________________';
        page.drawText(value, {
          x: x,
          y: fieldY - 10,
          size: 9,
          font,
          color: black,
        });
      });

      yPosition -= (Math.ceil(fields.length / columns) * 25) + 10;
    };

    // Function to wrap text
    const wrapText = (text, maxWidth, fontSize, font) => {
      const sanitizedText = sanitizeText(text);
      const lines = [];
      const words = sanitizedText.split(' ');
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine + ' ' + word;
        const testWidth = testLine.length * (fontSize * 0.5);
        
        if (testWidth < maxWidth) {
          currentLine = testLine;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    // FIXED: Improved function to embed signature image
    const embedSignatureImage = async (signatureData) => {
      if (!signatureData) {
        console.log('No signature data provided');
        return null;
      }
      
      try {
        console.log('Processing signature data');
        
        // Check if it's a data URL
        if (signatureData.startsWith('data:image')) {
          console.log('Processing signature as data URL');
          
          const base64Data = signatureData.split(',')[1];
          if (!base64Data) {
            console.warn('No base64 data found in signature');
            return null;
          }
          
          try {
            const signatureBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
            
            try {
              const image = await pdfDoc.embedPng(signatureBytes);
              console.log('Successfully embedded signature as PNG');
              return image;
            } catch (pngError) {
              console.log('Not a PNG, trying JPG...');
              try {
                const image = await pdfDoc.embedJpg(signatureBytes);
                console.log('Successfully embedded signature as JPG');
                return image;
              } catch (jpgError) {
                console.warn('Could not embed signature as PNG or JPG');
                return null;
              }
            }
          } catch (error) {
            console.warn('Error processing signature data URL:', error);
            return null;
          }
        }
        
        // Check if it's a URL to an image in storage
        if (signatureData.startsWith('https://') || signatureData.startsWith('http://')) {
          console.log('Fetching signature from URL:', signatureData);
          try {
            // Use the original URL without cache busting for Firebase Storage
            const response = await fetch(signatureData);
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const arrayBuffer = await response.arrayBuffer();
            const signatureBytes = new Uint8Array(arrayBuffer);
            
            try {
              const image = await pdfDoc.embedPng(signatureBytes);
              console.log('Successfully embedded signature from URL as PNG');
              return image;
            } catch (pngError) {
              try {
                const image = await pdfDoc.embedJpg(signatureBytes);
                console.log('Successfully embedded signature from URL as JPG');
                return image;
              } catch (jpgError) {
                console.warn('Could not embed signature from URL as PNG or JPG');
                return null;
              }
            }
          } catch (fetchError) {
            console.warn('Could not fetch signature from URL:', fetchError);
            return null;
          }
        }
        
        console.warn('Unknown signature format');
        return null;
      } catch (error) {
        console.warn('Error embedding signature image:', error);
        return null;
      }
    };

    // ✅ UPDATED: Function to draw signature area with notices for both pickup and delivery
    const drawSignatureArea = async (title, signatureInfo, signatureType, notice, step) => {
      checkPageSpace(180);
      
      // Section header
      page.drawRectangle({
        x: 40,
        y: yPosition - 20,
        width: width - 80,
        height: 20,
        color: lightGray,
      });

      page.drawText(sanitizeText(title), {
        x: 50,
        y: yPosition - 14,
        size: 10,
        font: boldFont,
        color: black,
      });

      yPosition -= 30;

      // Add signature notice if provided
      if (notice) {
        const noticeLines = wrapText(notice, width - 100, 7, font);
        noticeLines.forEach(line => {
          checkPageSpace(10);
          page.drawText(line, {
            x: 50,
            y: yPosition,
            size: 7,
            font,
            color: darkGray,
          });
          yPosition -= 9;
        });
        yPosition -= 5;
      }

      // Signature fields
      const fields = [
        { 
          label: `${signatureType.toUpperCase()} NAME (TYPED)`, 
          value: signatureInfo.name || '_________________________' 
        },
        { 
          label: 'DATE', 
          value: signatureInfo.date || new Date().toLocaleDateString() 
        }
      ];

      // Draw name and date fields
      fields.forEach((field, index) => {
        page.drawText(sanitizeText(field.label), {
          x: 50,
          y: yPosition - (index * 25),
          size: 8,
          font: boldFont,
          color: darkGray,
        });

        page.drawText(sanitizeText(field.value), {
          x: 50,
          y: yPosition - (index * 25) - 10,
          size: 9,
          font,
          color: black,
        });
      });

      yPosition -= 60;

      // Signature line and label
      page.drawLine({
        start: { x: 50, y: yPosition },
        end: { x: width - 50, y: yPosition },
        thickness: 1,
        color: black,
      });

      page.drawText(`${signatureType.toUpperCase()} SIGNATURE (${step.toUpperCase()})`, {
        x: 50,
        y: yPosition - 12,
        size: 8,
        font: boldFont,
        color: darkGray,
      });

      yPosition -= 30;

      // Try to embed signature image if available
      if (signatureInfo.signature) {
        console.log(`Attempting to embed ${signatureType} ${step} signature`);
        
        const signatureImage = await embedSignatureImage(signatureInfo.signature);
        
        if (signatureImage) {
          console.log(`Successfully embedded ${signatureType} ${step} signature image`);
          
          const maxWidth = 180;
          const maxHeight = 60;
          const scale = Math.min(maxWidth / signatureImage.width, maxHeight / signatureImage.height);
          
          const signatureWidth = signatureImage.width * scale;
          const signatureHeight = signatureImage.height * scale;
          
          page.drawImage(signatureImage, {
            x: 50,
            y: yPosition - signatureHeight,
            width: signatureWidth,
            height: signatureHeight,
          });
          
          yPosition -= signatureHeight + 20;
        } else {
          console.warn(`Could not embed ${signatureType} ${step} signature image - showing placeholder`);
          page.drawText('[DIGITAL SIGNATURE CAPTURED]', {
            x: 50,
            y: yPosition - 10,
            size: 9,
            font: boldFont,
            color: rgb(0, 0.5, 0),
          });
          yPosition -= 30;
        }
      } else {
        console.log(`No ${signatureType} ${step} signature available`);
        page.drawText('_________________________', {
          x: 50,
          y: yPosition - 10,
          size: 9,
          font,
          color: black,
        });
        yPosition -= 30;
      }

      yPosition -= 10;
    };

    // FIXED: Improved photo embedding function with CORS proxy
    const embedPhotoFromURL = async (photoUrl) => {
      if (!photoUrl) {
        console.log('No photo URL provided');
        return null;
      }
      
      try {
        console.log('Attempting to embed photo from URL:', photoUrl);
        
        // Use a CORS proxy for Firebase Storage URLs to avoid CORS issues
        let fetchUrl = photoUrl;
        
        // If it's a Firebase Storage URL and we're having CORS issues, use a proxy
        if (photoUrl.includes('firebasestorage.googleapis.com')) {
          // You can use a CORS proxy service or your own proxy
          // For now, we'll try direct fetch first, then fallback
          fetchUrl = photoUrl;
        }
        
        const response = await fetch(fetchUrl, {
          mode: 'cors',
          headers: {
            'Accept': 'image/*',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        const photoBytes = new Uint8Array(arrayBuffer);
        
        // Check if it's a JPEG by looking for JPEG markers
        const isJPEG = photoBytes[0] === 0xFF && photoBytes[1] === 0xD8;
        const isPNG = photoBytes[0] === 0x89 && photoBytes[1] === 0x50 && photoBytes[2] === 0x4E && photoBytes[3] === 0x47;
        
        console.log('Image format detection:', { isJPEG, isPNG, bytesLength: photoBytes.length });
        
        try {
          if (isJPEG) {
            const image = await pdfDoc.embedJpg(photoBytes);
            console.log('Successfully embedded photo as JPG');
            return image;
          } else if (isPNG) {
            const image = await pdfDoc.embedPng(photoBytes);
            console.log('Successfully embedded photo as PNG');
            return image;
          } else {
            // Try both formats if detection fails
            try {
              const image = await pdfDoc.embedJpg(photoBytes);
              console.log('Successfully embedded photo as JPG (fallback)');
              return image;
            } catch (jpgError) {
              const image = await pdfDoc.embedPng(photoBytes);
              console.log('Successfully embedded photo as PNG (fallback)');
              return image;
            }
          }
        } catch (embedError) {
          console.warn('Could not embed photo in any format:', embedError);
          return null;
        }
      } catch (error) {
        console.warn('Error embedding photo from URL:', photoUrl, error);
        
        // Fallback: Try with CORS proxy
        if (photoUrl.includes('firebasestorage.googleapis.com')) {
          try {
            console.log('Trying CORS proxy for Firebase Storage URL...');
            const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(photoUrl)}`;
            const response = await fetch(proxyUrl);
            
            if (response.ok) {
              const arrayBuffer = await response.arrayBuffer();
              const photoBytes = new Uint8Array(arrayBuffer);
              
              try {
                const image = await pdfDoc.embedJpg(photoBytes);
                console.log('Successfully embedded photo via CORS proxy');
                return image;
              } catch (jpgError) {
                const image = await pdfDoc.embedPng(photoBytes);
                console.log('Successfully embedded photo via CORS proxy as PNG');
                return image;
              }
            }
          } catch (proxyError) {
            console.warn('CORS proxy also failed:', proxyError);
          }
        }
        
        return null;
      }
    };

    // FIXED: Improved photo section that properly handles photo display
    const drawPhotoSection = async (title, photos, location) => {
      checkPageSpace(200);
      
      // Section header
      page.drawRectangle({
        x: 40,
        y: yPosition - 20,
        width: width - 80,
        height: 20,
        color: lightGray,
      });

      page.drawText(sanitizeText(title), {
        x: 50,
        y: yPosition - 14,
        size: 10,
        font: boldFont,
        color: black,
      });

      yPosition -= 30;

      if (photos.length === 0) {
        page.drawText('No photos captured', {
          x: 50,
          y: yPosition,
          size: 9,
          font,
          color: darkGray,
        });
        yPosition -= 20;
        return;
      }

      // Display photo count and names
      page.drawText(`${photos.length} photo(s) captured:`, {
        x: 50,
        y: yPosition,
        size: 9,
        font: boldFont,
        color: black,
      });
      yPosition -= 15;

      // List photo names (truncate if too many)
      const maxDisplayNames = 3;
      photos.slice(0, maxDisplayNames).forEach((photo, index) => {
        const photoName = photo.name || `Photo ${index + 1}`;
        page.drawText(`• ${sanitizeText(photoName)}`, {
          x: 60,
          y: yPosition,
          size: 8,
          font,
          color: darkGray,
        });
        yPosition -= 10;
      });

      if (photos.length > maxDisplayNames) {
        page.drawText(`• ...and ${photos.length - maxDisplayNames} more photos`, {
          x: 60,
          y: yPosition,
          size: 8,
          font,
          color: darkGray,
        });
        yPosition -= 10;
      }

      yPosition -= 10;

      // Try to embed first photo as a visual representation
      const firstPhoto = photos[0];
      
      if (firstPhoto && firstPhoto.url) {
        checkPageSpace(120);
        
        page.drawText('Photo Preview:', {
          x: 50,
          y: yPosition,
          size: 8,
          font: boldFont,
          color: black,
        });
        yPosition -= 10;

        try {
          console.log(`Attempting to embed ${location} photo:`, firstPhoto.name, firstPhoto.url);
          const embeddedImage = await embedPhotoFromURL(firstPhoto.url);
          
          if (embeddedImage) {
            const maxWidth = width - 100;
            const maxHeight = 100;
            const scale = Math.min(maxWidth / embeddedImage.width, maxHeight / embeddedImage.height);
            
            const imageWidth = embeddedImage.width * scale;
            const imageHeight = embeddedImage.height * scale;
            
            // Draw image border
            page.drawRectangle({
              x: 50,
              y: yPosition - imageHeight - 2,
              width: imageWidth + 4,
              height: imageHeight + 4,
              borderColor: rgb(0.8, 0.8, 0.8),
              borderWidth: 1,
            });
            
            // Draw the actual image
            page.drawImage(embeddedImage, {
              x: 52,
              y: yPosition - imageHeight,
              width: imageWidth,
              height: imageHeight,
            });
            
            yPosition -= imageHeight + 15;
            console.log(`✅ Successfully embedded ${location} photo: ${firstPhoto.name}`);
          } else {
            // If embedding fails, show detailed placeholder
            page.drawText('[Photo Preview Unavailable - Embedding Failed]', {
              x: 50,
              y: yPosition,
              size: 8,
              font,
              color: rgb(0.8, 0, 0),
            });
            yPosition -= 15;
            
            // Draw a placeholder box
            page.drawRectangle({
              x: 50,
              y: yPosition - 60,
              width: 100,
              height: 60,
              borderColor: rgb(0.8, 0.8, 0.8),
              borderWidth: 1,
              color: rgb(0.95, 0.95, 0.95),
            });
            
            page.drawText('No Preview', {
              x: 75,
              y: yPosition - 35,
              size: 7,
              font,
              color: darkGray,
            });
            
            yPosition -= 80;
          }
        } catch (error) {
          console.warn(`❌ Failed to embed ${location} photo ${firstPhoto.name}:`, error);
          page.drawText('[Photo embedding failed]', {
            x: 50,
            y: yPosition,
            size: 8,
            font,
            color: rgb(0.8, 0, 0),
          });
          yPosition -= 15;
        }
      } else {
        page.drawText('[Photo URL missing]', {
          x: 50,
          y: yPosition,
          size: 8,
          font,
          color: rgb(0.8, 0, 0),
        });
        yPosition -= 15;
      }

      yPosition -= 10;
    };

    // Professional Header
    page.drawRectangle({
      x: 0,
      y: height - 60,
      width: width,
      height: 60,
      color: lightGray,
    });

    page.drawText(`Bill Of Lading #${latestShipment.invoiceNumber}`, {
      x: 50,
      y: height - 25,
      size: 12,
      font: boldFont,
      color: black,
    });

    page.drawText('This Bill of Lading is issued by Flex Logistics Management LLC for company customers only.', {
      x: 50,
      y: height - 45,
      size: 8,
      font,
      color: darkGray,
    });

    yPosition = height - 90;

    // ✅ FIXED: 1. SHIPMENT INFORMATION - Using the actual displayId
    drawFormSection('1 SHIPMENT INFORMATION', [
      { label: 'SHIPMENT ID', value: displayId }, // ✅ Using the actual displayId
      { label: 'ITEM DESCRIPTION', value: latestShipment.itemDescription }
    ], 2);

    // 2. SHIPS FROM
    drawFormSection('2 SHIPS FROM', [
      { label: 'CONTACT INFORMATION', value: latestShipment.userName || latestShipment.name },
      { label: 'STREET ADDRESS', value: latestShipment.pickupAddress?.split('\n')[0] },
      { label: 'PHONE NUMBER', value: latestShipment.phone }
    ], 2);

    // 3. SHIPS TO
    drawFormSection('3 SHIPS TO', [
      { label: 'CONTACT INFORMATION', value: latestShipment.userName || latestShipment.name },
      { label: 'STREET ADDRESS', value: latestShipment.dropoffAddress?.split('\n')[0] }
    ], 2);

    // 4. CARRIER INFORMATION
    drawFormSection('4 CARRIER INFORMATION', [
      { label: 'CARRIER COMPANY NAME LLC', value: 'Flex Logistics Management LLC' },
      { label: 'CARRIER NAME', value: 'Flex Logistics Management' },
    ], 2);

    // 5. PAYMENT TERMS
    const paymentStatus = latestShipment.paymentMethod === 'Stripe' ? 'PREPAID - CREDIT CARD' : 
                         latestShipment.paymentVerified ? 'PREPAID - VERIFIED' : 'COLLECT FROM CUSTOMER';
    
    drawFormSection('5 PAYMENT TERMS', [
      { label: 'PAYMENT TERMS', value: `X ${paymentStatus}` },
      { label: 'AGREED PRICE', value: `$${parseFloat(latestShipment.agreedPrice || 0).toFixed(2)}` }
    ], 2);

    // 6. ADDITIONAL SERVICES/SPECIAL INSTRUCTIONS
    if (latestShipment.specialInstructions) {
      drawFormSection('6 ADDITIONAL SERVICES/SPECIAL INSTRUCTIONS', [
        { label: 'INSTRUCTIONS', value: latestShipment.specialInstructions }
      ], 1);
    }

    // 7. PHOTO DOCUMENTATION SECTION
    checkPageSpace(100);
    page.drawText('7 PHOTO DOCUMENTATION', {
      x: 50,
      y: yPosition,
      size: 10,
      font: boldFont,
      color: black,
    });
    yPosition -= 15;

    // Pickup Photos
    if (pickupPhotos.length > 0) {
      await drawPhotoSection('PICKUP PHOTOS:', pickupPhotos, 'pickup');
    } else {
      page.drawText('PICKUP PHOTOS: No photos captured', {
        x: 50,
        y: yPosition,
        size: 9,
        font,
        color: darkGray,
      });
      yPosition -= 20;
    }

    // Delivery Photos
    if (deliveryPhotos.length > 0) {
      checkPageSpace(50);
      page.drawText('DELIVERY PHOTOS:', {
        x: 50,
        y: yPosition,
        size: 9,
        font: boldFont,
        color: black,
      });
      yPosition -= 15;
      
      await drawPhotoSection('', deliveryPhotos, 'delivery');
    } else {
      page.drawText('DELIVERY PHOTOS: No photos captured', {
        x: 50,
        y: yPosition,
        size: 9,
        font,
        color: darkGray,
      });
      yPosition -= 20;
    }

    // 8. PICKUP COMMENTS SECTION
    if (pickupComment) {
      checkPageSpace(50);
      page.drawText('8 PICKUP COMMENTS', {
        x: 50,
        y: yPosition,
        size: 10,
        font: boldFont,
        color: black,
      });
      yPosition -= 15;

      const pickupCommentLines = wrapText(pickupComment, width - 100, 8, font);
      pickupCommentLines.forEach(line => {
        checkPageSpace(10);
        page.drawText(line, {
          x: 50,
          y: yPosition,
          size: 8,
          font,
          color: black,
        });
        yPosition -= 10;
      });
      yPosition -= 10;
    }

    // 9. DELIVERY COMMENTS SECTION
    if (deliveryComment) {
      checkPageSpace(50);
      page.drawText(deliveryComment ? '9 DELIVERY COMMENTS' : '8 DELIVERY COMMENTS', {
        x: 50,
        y: yPosition,
        size: 10,
        font: boldFont,
        color: black,
      });
      yPosition -= 15;

      const deliveryCommentLines = wrapText(deliveryComment, width - 100, 8, font);
      deliveryCommentLines.forEach(line => {
        checkPageSpace(10);
        page.drawText(line, {
          x: 50,
          y: yPosition,
          size: 8,
          font,
          color: black,
        });
        yPosition -= 10;
      });
      yPosition -= 10;
    }

    // ✅ UPDATED: 10. PICKUP SIGNATURES SECTION
    const pickupDriverSignatureSectionNumber = deliveryComment ? '10' : (pickupComment ? '9' : '8');
    const pickupDriverNotice = "Carrier acknowledges receipt of the above described goods. Carrier certifies that the goods have been inspected and are received in the condition described, except as noted. Carrier assumes responsibility for safe transport to destination.";

    await drawSignatureArea(
      `${pickupDriverSignatureSectionNumber} DRIVER NAME & SIGNATURE `, 
      {
        name: latestShipment.assignedDriver?.driverName || 'Driver Name',
        date: new Date().toLocaleDateString(),
        signature: pickupDriverSignature
      }, 
      'driver',
      pickupDriverNotice,
      'pickup'
    );

    // ✅ UPDATED: 11. CUSTOMER PICKUP SIGNATURE
    const pickupCustomerSignatureSectionNumber = deliveryComment ? '11' : (pickupComment ? '10' : '9');
    const pickupCustomerNotice = "I hereby certify that the goods described above have been received by the carrier in apparent good order and condition, except as noted. I authorize the carrier to transport these goods to the destination specified.";

    await drawSignatureArea(
      `${pickupCustomerSignatureSectionNumber} CUSTOMER NAME & SIGNATURE`, 
      {
        name: latestShipment.userName || latestShipment.name || 'Customer Name',
        date: new Date().toLocaleDateString(),
        signature: pickupCustomerSkipped ? null : pickupCustomerSignature
      }, 
      pickupCustomerSkipped ? 'customer (not present)' : 'customer',
      pickupCustomerNotice,
      'pickup'
    );

    if (pickupCustomerSkipped) {
      page.drawText('CUSTOMER NOT PRESENT AT PICKUP - SIGNATURE SKIPPED', {
        x: 50,
        y: yPosition - 5,
        size: 9,
        font: boldFont,
        color: rgb(0.8, 0, 0),
      });
      yPosition -= 15;
    }

    // ✅ UPDATED: 12. DRIVER SIGNATURE (DELIVERY) with notice
    const deliveryDriverSignatureSectionNumber = deliveryComment ? '12' : (pickupComment ? '11' : '10');
    const deliveryDriverNotice = "Carrier acknowledges receipt of packages and required placards. Carrier certifies emergency response information was made available and/or carrier has the DOT emergency response guidebook or equivalent documentation in the vehicle. Property described above is received in good order, except as noted.";
    
    await drawSignatureArea(
      `${deliveryDriverSignatureSectionNumber} DRIVER NAME & SIGNATURE`, 
      {
        name: latestShipment.assignedDriver?.driverName || 'Driver Name',
        date: new Date().toLocaleDateString(),
        signature: deliveryDriverSignature
      }, 
      'driver',
      deliveryDriverNotice,
      'delivery'
    );

    // ✅ UPDATED: 13. CUSTOMER SIGNATURE (DELIVERY) with notice
    const deliveryCustomerSignatureSectionNumber = deliveryComment ? '13' : (pickupComment ? '12' : '11');
    const deliveryCustomerNotice = "This is to certify that the above named materials are properly classified, packaged, marked and labeled, and are in proper condition for transportation according to the applicable regulations of the DOT. I acknowledge receipt of the goods in the condition described.";
    
    await drawSignatureArea(
      `${deliveryCustomerSignatureSectionNumber} CUSTOMER NAME & SIGNATURE`, 
      {
        name: latestShipment.userName || latestShipment.name || 'Customer Name',
        date: new Date().toLocaleDateString(),
        signature: deliveryCustomerSkipped ? null : deliveryCustomerSignature
      }, 
      deliveryCustomerSkipped ? 'customer (not present)' : 'customer',
      deliveryCustomerNotice,
      'delivery'
    );

    if (deliveryCustomerSkipped) {
      page.drawText('CUSTOMER NOT PRESENT AT DELIVERY - SIGNATURE SKIPPED', {
        x: 50,
        y: yPosition - 5,
        size: 9,
        font: boldFont,
        color: rgb(0.8, 0, 0),
      });
      yPosition -= 15;
    }

    // Update page numbers
    const pageCount = pdfDoc.getPages().length;
    pdfDoc.getPages().forEach((page, index) => {
      page.drawText(`PAGE ${index + 1} OF ${pageCount}`, {
        x: width - 100,
        y: 30,
        size: 8,
        font,
        color: darkGray,
      });
    });

    page.drawText('POWERED BY FLEX LOGISTICS MANAGEMENT LLC', {
      x: 50,
      y: 30,
      size: 8,
      font,
      color: darkGray,
    });

    // Serialize and upload PDF
    const pdfBytes = await pdfDoc.save();
    const storageRef = ref(storage, `bols/${latestShipment.id}/professional-BOL-${latestShipment.invoiceNumber}.pdf`);
    
    // ✅ UPDATED: Check if all required signatures are present for customer BOL viewing
    const hasRequiredSignatures = 
      (pickupDriverSignature && (pickupCustomerSignature || pickupCustomerSkipped)) &&
      (deliveryDriverSignature && (deliveryCustomerSignature || deliveryCustomerSkipped));
    
    const canCustomerViewBOL = latestShipment.status === 'delivered' && hasRequiredSignatures;
    
    const metadata = {
      customMetadata: {
        customerUserId: latestShipment.userId || 'unknown',
        customerCanViewBOL: canCustomerViewBOL ? 'true' : 'false',
        generatedBy: currentUser.uid,
        invoiceNumber: latestShipment.invoiceNumber,
        shipmentId: displayId, // ✅ Store the display ID for reference
        // Pickup signatures
        pickupDriverSigned: pickupDriverSignature ? 'true' : 'false',
        pickupCustomerSigned: pickupCustomerSignature ? 'true' : 'false',
        pickupCustomerSkipped: pickupCustomerSkipped ? 'true' : 'false',
        // Delivery signatures
        deliveryDriverSigned: deliveryDriverSignature ? 'true' : 'false',
        deliveryCustomerSigned: deliveryCustomerSignature ? 'true' : 'false',
        deliveryCustomerSkipped: deliveryCustomerSkipped ? 'true' : 'false',
        // Photos
        pickupPhotosCount: pickupPhotos.length.toString(),
        deliveryPhotosCount: deliveryPhotos.length.toString(),
        hasPickupComment: pickupComment ? 'true' : 'false',
        hasDeliveryComment: deliveryComment ? 'true' : 'false',
        embeddedSignatures: 'true',
        embeddedPhotos: 'true',
        lastUpdated: new Date().toISOString()
      }
    };

    await uploadBytes(storageRef, pdfBytes, metadata);
    const downloadURL = await getDownloadURL(storageRef);

    await updateDoc(doc(db, 'shipments', latestShipment.id), {
      bolUrl: downloadURL,
      bolGeneratedAt: serverTimestamp(),
      customerCanViewBOL: canCustomerViewBOL,
      updatedAt: serverTimestamp(),
      bolGeneratedBy: currentUser.uid,
    });

    console.log('Professional BOL generated successfully with embedded photos and signatures:', {
      // Pickup signatures
      pickupDriverSigned: !!pickupDriverSignature,
      pickupCustomerSigned: !!pickupCustomerSignature,
      pickupCustomerSkipped,
      // Delivery signatures
      deliveryDriverSigned: !!deliveryDriverSignature,
      deliveryCustomerSigned: !!deliveryCustomerSignature,
      deliveryCustomerSkipped,
      // Overall
      customerCanView: canCustomerViewBOL,
      pages: pageCount,
      shipmentId: displayId,
      pickupPhotos: pickupPhotos.length,
      deliveryPhotos: deliveryPhotos.length,
      embeddedPhotos: 'SUCCESS'
    });

    return downloadURL;

  } catch (error) {
    console.error('Error generating professional BOL:', error);
    throw error;
  }
};

// ✅ ADD THIS: Helper function to generate display ID
const generateDisplayId = (firebaseId, index) => {
  if (firebaseId && firebaseId.includes('_')) {
    const parts = firebaseId.split('_');
    if (parts.length >= 3) {
      const timestamp = parts[1];
      const randomPart = parts[2];
      return `SH${timestamp.slice(-6)}${randomPart.substring(0, 4).toUpperCase()}`;
    }
  }
  return `SH${String(index + 1).padStart(6, '0')}`;
};

export const generateBOL = generateProfessionalBOL;